<script lang="ts">
	import { browser } from '$app/environment';
	import { base } from '$app/paths';
	import { navigating, page } from '$app/stores';
	import Footer from '$lib/Footer.svelte';
	import Header from '$lib/Header.svelte';
	import SvgSprite from '$lib/SvgSprite.svelte';
	import Toast from '$lib/Toast.svelte';
	import * as api from '$lib/shared/apis';
	import {
		filterIsVisible,
		filteredFrom,
		filteredPersons,
		filteredTask,
		filteredTo,
		query,
	} from '$lib/shared/filter';
	import { getNotifications } from '$lib/shared/notifications';
	import { isAuthenticated, pending, user } from '$lib/stores/auth';
	import { height } from '$lib/stores/header';
	import { campaignsExpanded, footerIsVisible, nav, siteTitle } from '$lib/stores/nav';
	import { notifications } from '$lib/stores/notifications';
	import { submissionFormIsVisible } from '$lib/stores/submission';
	import { toastMessage } from '$lib/stores/toast';
	import { innerWidth, screen } from '$lib/stores/width';
	import * as Sentry from '@sentry/browser';
	import { onMount } from 'svelte';
	import 'wicg-inert';
	import '../../app.css';
	import { goto } from '$app/navigation';
	import { extraErrorDataIntegration } from '@sentry/integrations';

	Sentry.init({
		dsn: 'https://7d8b271a7d1a4532a3abdb18453b8b3d@sentry.urbantrout.io/26',
		environment: import.meta.env.VITE_PROJECT_NAME,
		integrations: [new Sentry.BrowserTracing(), extraErrorDataIntegration({ depth: 10 })],
		tracesSampleRate: 1.0,
	});

	export let data;

	let siteDescription = `Tauchen Sie in die Welt der Jahre ${data.project.fromYear}–${data.project.toYear} ein und unterstützen Sie uns bei der Transkription unserer Briefe.`;

	let screenSizeWarningIsVisible = false;
	onMount(async () => {
		$pending = true;

		const { response, json } = await api.post({
			query: /* GraphQL */ `
				query ownUser($projectName: ProjectName!) {
					ownUser(project: $projectName) {
						id
						name
						role
						lockedObjects {
							id
						}
						notificationSettings {
							info
							receivedApproval
							newerSubmission
						}
						facebook
						instagram
						twitter
						termsAccepted
					}
				}
			`,
			variables: {
				projectName: import.meta.env.VITE_PROJECT_NAME,
			},
		});

		if (response.ok) {
			$isAuthenticated = !!json.data.ownUser;
			$user = json.data.ownUser;
			$pending = false;
		}
	});

	let interval: ReturnType<typeof setInterval>;

	const pollNotifications = async () => {
		let newNotifications = await getNotifications(['UNREAD', 'READ'], { size: 20, skip: 0 });
		if (newNotifications) {
			$notifications = newNotifications;
		}
		interval = setInterval(async () => {
			newNotifications = await getNotifications(['UNREAD', 'READ'], { size: 20, skip: 0 });
			if (newNotifications) {
				$notifications = newNotifications;
			}
		}, 60000);
	};

	const clearNotifications = () => {
		clearInterval(interval);
	};

	$: if ($isAuthenticated) {
		pollNotifications();
	} else {
		clearNotifications();
	}
	$: if ($user && !$user.termsAccepted) {
		if (
			![
				'/(app)/meine-beitraege',
				'/(app)/nutzungsbedingungen',
				'/(app)/datenschutz',
			].includes($page.route.id)
		) {
			goto(`${base}/meine-beitraege#einstellungen`);
		}
	}

	$: screenSizeWarningIsVisible =
		browser &&
		$screen === 'mobile' &&
		![
			`${base}/`,
			`${base}/datenschutz/`,
			`${base}/impressum/`,
			`${base}/nutzungsbedingungen/`,
			`${base}/barrierefreiheit/`,
		].includes($page.url.pathname);
	$: $navigating && nav.hide();
	$: title = `${$siteTitle ? $siteTitle + ' - ' : ''}Wienbibliothek im Rathaus Crowdsourcing`;

	let main;

	$: if ($navigating) {
		document.documentElement.classList.add('scroll-behavior-auto');
		setTimeout(() => {
			document.documentElement.classList.remove('scroll-behavior-auto');
		}, 500);
		// Reset filters if use leaves object-index or object page.
		if (
			$navigating.to.route.id !== '/(app)/briefe' &&
			$navigating.to.route.id !== '/(app)/object/[id]'
		) {
			setTimeout(() => {
				$query = '';
				$filteredPersons = [];
				$filteredFrom = '';
				$filteredTo = '';
				$filteredTask = '';
				$filterIsVisible = false;
			}, 500);
		}
	}

	$: main && main.toggleAttribute('inert', $nav);
	let skipLink;
	$: skipLink && skipLink.toggleAttribute('inert', $nav);
	$: if ($footerIsVisible) {
		if (browser) {
			document.body.classList.remove('bg-beige');
		}
	} else {
		if (browser) {
			document.body.classList.add('bg-beige');
		}
	}
</script>

<svelte:head>
	<title>{title}</title>
	<meta name="description" content={siteDescription} />
	<meta property="og:title" content={title} />
	<meta property="og:image" content="{data.project.host}{base}/img/og-image.png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:description" content={siteDescription} />
	<meta property="og:type" content="website" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:image" content="{data.project.host}{base}/img/og-image.png" />
</svelte:head>

<svelte:window bind:innerWidth={$innerWidth} />

<a href="#main" class="skip-link" bind:this={skipLink}>Zum Hauptinhalt wechseln</a>

<SvgSprite />

<Header project={data.project} projects={data.projects} />

<main
	class="px-5 md:px-6 flex-auto"
	bind:this={main}
	id="main"
	style:--header-height="{$height}px"
	class:opacity-20={$campaignsExpanded}
>
	{#if screenSizeWarningIsVisible}
		<div class="flex flex-col justify-center py-20">
			<h1 class="text-lg-fluid">Browserfenster zu klein</h1>
			<p class="mt-6">
				Ihr Browserfenster ist zu klein um den vollen Inhalt darzustellen. Bitte vergrößern
				Sie Ihr Browserfenster oder wechseln auf ein Gerät mit größerem Display
			</p>
		</div>
	{:else}
		<slot />
	{/if}
</main>

{#if $footerIsVisible && !$submissionFormIsVisible}
	<Footer />
{/if}

{#if $toastMessage}
	<Toast />
{/if}

<div id="modals" />
